import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Mail, ChevronLeft, Copy } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { listContacts, type ContactRow } from "@/lib/contacts.functions";

export const Route = createFileRoute("/_authenticated/admin_/contacts")({
  head: () => ({
    meta: [
      { title: "お問い合わせ管理 | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminContactsPage,
});

function AdminContactsPage() {
  const listFn = useServerFn(listContacts);
  const [rows, setRows] = useState<ContactRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ContactRow | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!data.session) {
          setError("未ログインです");
          return;
        }
        const headers = { Authorization: `Bearer ${data.session.access_token}` };
        const res = await listFn({ headers });
        setRows(res);
      } catch (e) {
        setError(e instanceof Error ? e.message : "取得に失敗しました");
      }
    })();
  }, [listFn]);

  return (
    <main className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <Button asChild variant="ghost" size="sm" className="mb-2 -ml-2">
              <Link to="/admin">
                <ChevronLeft className="mr-1 h-4 w-4" />
                管理画面に戻る
              </Link>
            </Button>
            <h1 className="text-2xl font-bold tracking-tight">お問い合わせ管理</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              直近200件まで表示。返信は記載のメールアドレス宛に手動で送ってください。
            </p>
          </div>
        </div>

        {error && (
          <Card className="border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
            {error}
          </Card>
        )}

        {!rows && !error && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        {rows && rows.length === 0 && (
          <Card className="p-10 text-center text-muted-foreground">
            <Mail className="mx-auto mb-3 h-8 w-8 opacity-60" />
            お問い合わせはまだありません。
          </Card>
        )}

        {rows && rows.length > 0 && (
          <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
            <Card className="divide-y divide-border/60 overflow-hidden">
              <ul>
                {rows.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(r)}
                      className={`block w-full px-4 py-3 text-left transition hover:bg-muted/50 ${
                        selected?.id === r.id ? "bg-muted" : ""
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="truncate font-medium">{r.subject}</div>
                        {r.user_id ? (
                          <Badge variant="secondary" className="shrink-0 text-[10px]">
                            会員
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="shrink-0 text-[10px]">
                            ゲスト
                          </Badge>
                        )}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-muted-foreground">
                        {r.name} · {r.email}
                      </div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground">
                        {new Date(r.created_at).toLocaleString("ja-JP")}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="p-5">
              {selected ? (
                <div className="space-y-4">
                  <div>
                    <div className="text-xs text-muted-foreground">件名</div>
                    <h2 className="mt-1 text-lg font-semibold">{selected.subject}</h2>
                  </div>

                  <div className="grid gap-3 text-sm sm:grid-cols-2">
                    <Field label="お名前" value={selected.name} />
                    <Field
                      label="メール"
                      value={selected.email}
                      action={
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                          onClick={() => {
                            navigator.clipboard.writeText(selected.email);
                            toast.success("コピーしました");
                          }}
                        >
                          <Copy className="h-3 w-3" />
                          コピー
                        </button>
                      }
                    />
                    <Field
                      label="送信日時"
                      value={new Date(selected.created_at).toLocaleString("ja-JP")}
                    />
                    <Field label="ユーザーID" value={selected.user_id ?? "（未ログイン）"} />
                    <Field label="IP" value={selected.ip ?? "—"} />
                    <Field label="UA" value={selected.ua ?? "—"} />
                  </div>

                  <div>
                    <div className="text-xs text-muted-foreground">本文</div>
                    <div className="mt-1 whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-sm">
                      {selected.body}
                    </div>
                  </div>

                  <div className="pt-2">
                    <Button asChild>
                      <a
                        href={`mailto:${selected.email}?subject=${encodeURIComponent(
                          "Re: " + selected.subject,
                        )}`}
                      >
                        <Mail className="mr-2 h-4 w-4" />
                        メールで返信
                      </a>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="py-16 text-center text-sm text-muted-foreground">
                  左の一覧から問い合わせを選択してください。
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </main>
  );
}

function Field({
  label,
  value,
  action,
}: {
  label: string;
  value: string;
  action?: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">{label}</div>
        {action}
      </div>
      <div className="mt-0.5 break-all text-sm">{value}</div>
    </div>
  );
}
